import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
// import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
// import { userPermissions } from "@/contexts/userPermissions";
// import { PermissionEnum } from "@/utils/permissionEnum";
import { CiReceipt /* CiSearch */ } from "react-icons/ci";
import BackButton from "@/components/BackButton";
import { Field, FieldArray, Form, Formik, FormikProps } from "formik";
import { creditEngineSchema } from "./validations";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { CloseIcon } from "@chakra-ui/icons";
import { CreditEngineFormValues, CriteriaEngineByIdResponse, CriteriaRule, CriteriaSet } from "@/models/consignado/criteriaEngine.model";
import { getFormError } from "@/utils/getFIeldError";
import { createCriteriaEngine, deleteCriteriaEngine, deleteCriteriaRule, deleteCriteriaSet, getCriteriaEngineById, updateCriteriaEngine } from "@/services/consignado/criteriaEngine.service";
import { mapCriteriaEngineToPT } from "./criteriaEngine.mapper";
import { useNavigate, useParams } from "react-router-dom";
import { useMemo } from "react";
import { getCompatibleOperators, getDataSourcesForSelect, getFieldByValue, getFieldsForSelect, getOperatorsForSelect } from "@/utils/consignado/creditEngineConfig";

export default function ConsignadoCriterionEngineEditor() {
  const { criterioId } = useParams<{ criterioId: string }>()
  const isCreate: boolean = criterioId === undefined || criterioId === "novo";
  const navigate = useNavigate()
  const queryCache = useQueryClient()
  const operators = getOperatorsForSelect();
  const dataSources = getDataSourcesForSelect();
  // const { user } = useEnsureAuthenticated();
  // const permissions = userPermissions(user);
  const toast = useToast();
  const backgroundStyle = useConditionalBackground();

  const { data } = useQuery<CriteriaEngineByIdResponse>({
    queryKey: ['criteria-engine', criterioId],
    queryFn: async () => await getCriteriaEngineById(parseInt(criterioId!)),
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
    enabled: !isCreate
  })

  const initialValues = useMemo<CreditEngineFormValues>(() => {
    if (!data) {
      return {
        creditEngineName: "",
        creditEngineDescription: "",
        criteriaSets: [],
      };
    }

    return {
      id: data.id,
      creditEngineName: data.nome,
      creditEngineDescription: data.descricao,
      criteriaSets: data.conjuntos_criterios?.map((set) => ({
        id: set.id,
        name: set.nome ?? "",
        description: set.descricao ?? "",
        criteriaRules: set.criterios?.map((rule) => ({
          id: rule.id,
          field: rule.campo ?? "",
          operator: rule.operador ?? "",
          value: rule.valor ?? "",
          name: rule.nome ?? "",
          order: rule.ordem ?? "",
          dataSource: rule.fonte_dados ?? "",
        })),
      })) ?? [],
    };
  }, [data]);

  const handleCreateCriteriaEngine = useMutation({
    mutationFn: async (data: CreditEngineFormValues) => {
      const criteriaEngineMappedToPt = mapCriteriaEngineToPT(data)
      await createCriteriaEngine(criteriaEngineMappedToPt)
      return
    },
    onSuccess() {
      toast({
        title: "Sucesso",
        description: "Motor de crédito criado com successo",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate('/consignado/criterios-credito/')
    },
    onError() {
      toast({
        title: "Erro",
        description: "Erro ao criar motor de crédito",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  })

  const handleUpdateCriteriaEngine = useMutation({
    mutationFn: async (data: CreditEngineFormValues) => {
      const criteriaEngineMappedToPt = mapCriteriaEngineToPT(data)
      await updateCriteriaEngine(criteriaEngineMappedToPt)
      return
    },
    onSuccess() {
      toast({
        title: "Sucesso",
        description: "Motor de crédito atualizado com successo",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate('/consignado/criterios-credito/')
    },
    onError() {
      toast({
        title: "Erro",
        description: "Erro ao atualizar motor de crédito",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  })

  const handleDeleteCriteriaRule = useMutation({
    mutationFn: async (data: { id: string, criteriaSetIndex: number }) => {
      return await deleteCriteriaRule(data.id)
    },
    onSuccess(_, variables) {
      toast({
        title: "Sucesso",
        description: "Critério excluído com successo",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      const criteriaEngine = queryCache.getQueryData<CriteriaEngineByIdResponse>(['criteria-engine', criterioId])

      const newCriteriaRule = criteriaEngine?.conjuntos_criterios[variables.criteriaSetIndex].criterios.filter(rule => rule.id !== variables.id)

      const criteriaEngineUpdated = {
        ...criteriaEngine,
        conjuntos_criterios: criteriaEngine!.conjuntos_criterios.map((set, index) =>
          index === variables.criteriaSetIndex
            ? { ...set, criterios: newCriteriaRule }
            : set
        ),
      };

      queryCache.setQueryData(
        ["criteria-engine", criterioId],
        criteriaEngineUpdated
      );
    },
    onError() {
      toast({
        title: "Erro",
        description: "Erro ao excluir critério",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  })

  const handleDeleteCriteriaSet = useMutation({
    mutationFn: async (id: string) => {
      return await deleteCriteriaSet(id)
    },
    onSuccess(_, variables) {
      toast({
        title: "Sucesso",
        description: "Conjunto de critério excluído com successo",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      const criteriaEngine = queryCache.getQueryData<CriteriaEngineByIdResponse>(['criteria-engine', criterioId])

      const newCriteriaSets = criteriaEngine?.conjuntos_criterios.filter(set => set.id !== variables)

      const criteriaEngineUpdated = {
        ...criteriaEngine,
        conjuntos_criterios: newCriteriaSets
      };

      queryCache.setQueryData(
        ["criteria-engine", criterioId],
        criteriaEngineUpdated
      );
    },
    onError() {
      toast({
        title: "Erro",
        description: "Erro ao excluir conjunto de critérios",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  })

  const handleDeleteCriteriaEngine = useMutation({
    mutationFn: async (id: number) => {
      return await deleteCriteriaEngine(id)
    },
    onSuccess(_, variables) {
      toast({
        title: "Sucesso",
        description: "Motor de critérios excluído com successo",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      queryCache.removeQueries({
        queryKey: ['criteria-engine', variables],
      });
      queryCache.invalidateQueries({ queryKey: ['criteria-engines'] })
      navigate('/consignado/criterios-credito/')
    },
    onError() {
      toast({
        title: "Erro",
        description: "Erro ao excluir motor de critérios",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  })

  const handleOperator = (set: CriteriaSet, ruleIndex: number) => {
    const currentField = set.criteriaRules[ruleIndex]?.field;
    const currentDataSource = set.criteriaRules[ruleIndex]?.dataSource;
    let compatibleOps = operators;

    if (currentField && currentDataSource) {
      const field = getFieldByValue(currentDataSource, currentField);
      if (field) {
        compatibleOps = getCompatibleOperators(field)?.map((op) => ({
          value: op.valor,
          label: op.label,
        }));
      }
    }

    return compatibleOps?.map((op) => (
      <option key={op.value} value={op.value}>
        {op.label}
      </option>
    ));
  };

  const handleOrderCriteriaRuleDown = (
    set: CriteriaSet,
    ruleIndex: number,
    criteriaSetsIndex: number,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const rules = [...set.criteriaRules];
    const currentRule = rules[ruleIndex];
    const nextRule = rules[ruleIndex + 1];

    // Troca as posições
    rules[ruleIndex] = nextRule;
    rules[ruleIndex + 1] = currentRule;

    // Atualiza os orders
    rules.forEach((rule, idx) => {
      rule.order = idx + 1;
    });

    setFieldValue(`criteriaSets[${criteriaSetsIndex}].criteriaRules`, rules);
  };

  const handleOrderCriteriaRuleUp = (
    set: CriteriaSet,
    ruleIndex: number,
    criteriaSetsIndex: number,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const rules = [...set.criteriaRules];
    const currentRule = rules[ruleIndex];
    const prevRule = rules[ruleIndex - 1];

    // Troca as posições
    rules[ruleIndex] = prevRule;
    rules[ruleIndex - 1] = currentRule;

    // Atualiza os orders
    rules.forEach((rule, idx) => {
      rule.order = idx + 1;
    });

    setFieldValue(`criteriaSets[${criteriaSetsIndex}].criteriaRules`, rules);
  };

  return (
    <Box
      padding={{ base: "2", md: "4" }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        mb={4}
        color="white"
        boxShadow="md"
        width="100%"
      >
        <Flex
          alignItems={{ base: "center", md: "center" }}
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
          textAlign={{ base: "center", md: "left" }}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <CiReceipt size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              Editor de Critérios
            </Text>
          </Flex>
          <BackButton routeTo="/consignado/criterios-credito" />
        </Flex>
      </Box>

      <Box p={{ base: 4, md: 6 }} mb={4} boxShadow="md" width="100%">
        <Formik<CreditEngineFormValues>
          initialValues={initialValues}
          validationSchema={creditEngineSchema}
          enableReinitialize={true}
          // onSubmit={(values) => handleSubmitForm(values)}
          onSubmit={(values, { resetForm }) => {
            isCreate ? handleCreateCriteriaEngine.mutate(values, {
              onSuccess() {
                resetForm()
              }
            }) : handleUpdateCriteriaEngine.mutate(values)
          }
          }
        >
          {({ values, errors, touched, setFieldValue }: FormikProps<CreditEngineFormValues>) => (
            <Form style={{ width: "100%" }}>
              <FieldArray name="criteriaSets">
                {({ push }) => (
                  <>
                    <Flex
                      w="100%"
                      gap={5}
                      flexDir={["column", "column", "row", "row"]}
                      alignItems="start"
                    >
                      {/* campo fixo 1 */}
                      <Flex
                        w="100%"
                        flexWrap={["wrap", "wrap", "wrap", "nowrap"]}
                        alignItems='start'
                        gap={5}
                      >
                        <FormControl
                          isInvalid={!!getFormError(errors, ['creditEngineName']) && touched.creditEngineName}>
                          <FormLabel fontFamily="Lexend Deca" htmlFor="creditEngineName">
                            Nome do Motor de critérios
                          </FormLabel>
                          <Field
                            as={Input}
                            fontFamily="Lexend Deca"
                            id="creditEngineName"
                            name="creditEngineName"
                            type="text"
                            w="100%"
                          />

                          <FormErrorMessage>{errors?.creditEngineName}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!getFormError(errors, ['creditEngineDescription']) && touched.creditEngineDescription}>
                          <FormLabel
                            fontFamily="Lexend Deca"
                            htmlFor="creditEngineDescription"
                          >
                            Descrição do Motor de critérios
                          </FormLabel>
                          <Field
                            as={Input}
                            fontFamily="Lexend Deca"
                            id="creditEngineDescription"
                            name="creditEngineDescription"
                            type="text"
                            w="100%"
                          />
                          <FormErrorMessage>
                            {errors?.creditEngineDescription}
                          </FormErrorMessage>
                        </FormControl>
                      </Flex>

                      <Flex
                        width={["100%", "100%", "100%", "80%", "35%"]}
                        flexDir={["row", "row", "row", "row"]}
                        alignItems='end'
                        h='4.5rem'
                        gap={2}
                      >
                        <Button
                          px={2}
                          width="100%"
                          size="md"
                          type="button"
                          onClick={() => push({ name: "", description: "", dataSource: "", criteriaRules: [] } as CriteriaSet)}
                        >
                          Adicionar Conjunto de Critérios
                        </Button>

                        <Button
                          onClick={() => {
                            criterioId && !isCreate && handleDeleteCriteriaEngine.mutate(parseInt(criterioId))
                          }
                          }
                        >
                          <CloseIcon />
                        </Button>
                      </Flex>
                    </Flex>

                    <FormControl
                      isInvalid={
                        !!(getFormError(errors, ['criteriaSets']) && touched?.criteriaSets)
                      }
                      mt={4}
                    >
                      <FormErrorMessage>
                        {getFormError(errors, ['criteriaSets'])}
                      </FormErrorMessage>
                    </FormControl>

                    {/* === LISTA DINÂMICA === */}
                    <VStack width="100%" mt={6} align="start" spacing={4}>
                      {values?.criteriaSets?.map((set, criteriaSetsIndex) => (
                        <Box
                          key={criteriaSetsIndex}
                          w="100%"
                          p={4}
                          borderWidth={1}
                          borderRadius="md"
                          position="relative"
                        >
                          <Flex justify="space-between" align="center" mb={2}>
                            <Text fontWeight="500">
                              Conjunto de Critérios #{criteriaSetsIndex + 1}
                            </Text>
                            <CloseButton onClick={() => handleDeleteCriteriaSet.mutate(set?.id!)} />
                          </Flex>

                          {/* Campos do conjunto */}
                          <Flex alignItems='start' gap={5}>
                            <FormControl
                              isInvalid={
                                !!getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'name']) &&
                                touched.criteriaSets?.[criteriaSetsIndex]?.name
                              }
                            >
                              <FormLabel>Nome do Conjunto</FormLabel>
                              <Field
                                as={Input}
                                name={`criteriaSets[${criteriaSetsIndex}].name`}
                                placeholder="Ex: Dados dataprev e receita"
                              />
                              <FormErrorMessage>
                                {getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'name'])}
                              </FormErrorMessage>
                            </FormControl>

                            <FormControl
                              isInvalid={
                                !!getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'description']) &&
                                touched.criteriaSets?.[criteriaSetsIndex]?.description
                              }
                            >
                              <FormLabel>Descrição</FormLabel>
                              <Field
                                as={Input}
                                name={`criteriaSets[${criteriaSetsIndex}].description`}
                                placeholder="Descreva este conjunto"
                              />
                              <FormErrorMessage>
                                {getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'description'])}
                              </FormErrorMessage>
                            </FormControl>
                          </Flex>

                          {/* === LISTA DE CRITÉRIOS (criteriaRules) === */}
                          <FieldArray name={`criteriaSets[${criteriaSetsIndex}].criteriaRules`}>
                            {({ push }) => (
                              <Box mt={4}>
                                <Flex justify="space-between" align="center" mb={2}>
                                  <Text fontWeight="500">Regras do conjunto</Text>
                                  <Button
                                    size="sm"
                                    colorScheme="teal"
                                    onClick={() => {
                                      const nextOrder = (set.criteriaRules?.length || 0) + 1;
                                      push({ name: "", field: "", operator: "", dataSource: "", value: "", order: nextOrder } as CriteriaRule)
                                    }
                                    }
                                  >
                                    Adicionar Critério
                                  </Button>
                                </Flex>

                                <FormControl
                                  isInvalid={
                                    !!getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules'])
                                  }
                                  mt={2}
                                >
                                  <FormErrorMessage>
                                    {getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules'])}
                                  </FormErrorMessage>
                                </FormControl>

                                {set.criteriaRules?.map((_, ruleIndex) => {
                                  const isPercentageOp = [
                                    'PERCENTAGE_GREATER_EQUAL',
                                    'PERCENTAGE_LESS_EQUAL',
                                    'PERCENTAGE_GREATER',
                                    'PERCENTAGE_LESS',
                                    'PERCENTAGE_OF'
                                  ].includes(set.criteriaRules[ruleIndex]?.operator);

                                  return (
                                    <Flex
                                      key={ruleIndex}
                                      gap={2}
                                      align="start"
                                      borderWidth={1}
                                      borderRadius="md"
                                      p={3}
                                      mb={2}
                                    >
                                      <FormControl
                                        isInvalid={!!getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'name']) && touched.criteriaSets?.[criteriaSetsIndex]?.criteriaRules?.[ruleIndex]?.name}
                                        minW={isPercentageOp ? "140px" : "auto"}
                                        maxW={isPercentageOp ? "180px" : "auto"}
                                      >
                                        <FormLabel fontSize={isPercentageOp ? "sm" : "md"}>Título</FormLabel>
                                        <Field
                                          as={Input}
                                          name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].name`}
                                          placeholder="Ex: Idade maior que 20"
                                          size={isPercentageOp ? "sm" : "md"}
                                        />
                                        <FormErrorMessage>
                                          {getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'name'])}
                                        </FormErrorMessage>
                                      </FormControl>

                                      <FormControl
                                        isInvalid={
                                          !!getFormError(errors, [
                                            "criteriaSets",
                                            criteriaSetsIndex,
                                            "criteriaRules",
                                            ruleIndex,
                                            "dataSource",
                                          ]) &&
                                          touched.criteriaSets?.[
                                            criteriaSetsIndex
                                          ]?.criteriaRules?.[ruleIndex]
                                            ?.dataSource
                                        }
                                        minW={isPercentageOp ? "120px" : "auto"}
                                        maxW={isPercentageOp ? "150px" : "auto"}
                                      >
                                        <FormLabel fontSize={isPercentageOp ? "sm" : "md"}>Fonte de Dados</FormLabel>
                                        <Field
                                          as={Select}
                                          name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].dataSource`}
                                          placeholder={isPercentageOp ? "Fonte" : "Selecione uma fonte de dados"}
                                          size={isPercentageOp ? "sm" : "md"}
                                          onChange={(
                                            e: React.ChangeEvent<HTMLSelectElement>
                                          ) => {
                                            setFieldValue(
                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].dataSource`,
                                              e.target.value
                                            );

                                            setFieldValue(
                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].field`,
                                              ""
                                            );
                                            setFieldValue(
                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].operator`,
                                              ""
                                            );
                                          }}
                                        >
                                          {dataSources?.map((ds) => (
                                            <option
                                              key={ds.value}
                                              value={ds.value}
                                            >
                                              {ds.label}
                                            </option>
                                          ))}
                                        </Field>
                                        <FormErrorMessage>
                                          {getFormError(errors, [
                                            "criteriaSets",
                                            criteriaSetsIndex,
                                            "criteriaRules",
                                            ruleIndex,
                                            "dataSource",
                                          ])}
                                        </FormErrorMessage>
                                      </FormControl>

                                      <FormControl
                                        isInvalid={
                                          !!getFormError(errors, [
                                            "criteriaSets",
                                            criteriaSetsIndex,
                                            "criteriaRules",
                                            ruleIndex,
                                            "field",
                                          ]) &&
                                          touched.criteriaSets?.[
                                            criteriaSetsIndex
                                          ]?.criteriaRules?.[ruleIndex]?.field
                                        }
                                        minW={isPercentageOp ? "120px" : "auto"}
                                        maxW={isPercentageOp ? "150px" : "auto"}
                                      >
                                        <FormLabel fontSize={isPercentageOp ? "sm" : "md"}>Campo</FormLabel>
                                        <Field
                                          as={Select}
                                          name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].field`}
                                          placeholder={isPercentageOp ? "Campo" : "Selecione um campo"}
                                          size={isPercentageOp ? "sm" : "md"}
                                          disabled={
                                            !set.criteriaRules[ruleIndex]
                                              ?.dataSource
                                          }
                                          onChange={(
                                            e: React.ChangeEvent<HTMLSelectElement>
                                          ) => {
                                            setFieldValue(
                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].field`,
                                              e.target.value
                                            );
                                            // Limpa operador e valor quando o campo muda
                                            setFieldValue(
                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].operator`,
                                              ""
                                            );
                                            setFieldValue(
                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`,
                                              ""
                                            );
                                          }}
                                        >
                                          {set.criteriaRules[ruleIndex]
                                            ?.dataSource &&
                                            getFieldsForSelect(
                                              set.criteriaRules[ruleIndex]
                                                .dataSource
                                            )?.map((field) => (
                                              <option
                                                key={field.value}
                                                value={field.value}
                                              >
                                                {field.label}
                                              </option>
                                            ))}
                                        </Field>
                                        <FormErrorMessage>
                                          {getFormError(errors, [
                                            "criteriaSets",
                                            criteriaSetsIndex,
                                            "criteriaRules",
                                            ruleIndex,
                                            "field",
                                          ])}
                                        </FormErrorMessage>
                                      </FormControl>

                                      <FormControl
                                        isInvalid={
                                          !!getFormError(errors, [
                                            "criteriaSets",
                                            criteriaSetsIndex,
                                            "criteriaRules",
                                            ruleIndex,
                                            "operator",
                                          ]) &&
                                          touched.criteriaSets?.[
                                            criteriaSetsIndex
                                          ]?.criteriaRules?.[ruleIndex]
                                            ?.operator
                                        }
                                        minW={isPercentageOp ? "120px" : "auto"}
                                        maxW={isPercentageOp ? "180px" : "auto"}
                                      >
                                        <FormLabel fontSize={isPercentageOp ? "sm" : "md"}>Operador</FormLabel>
                                        <Field
                                          as={Select}
                                          name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].operator`}
                                          placeholder="Selecione"
                                          size={isPercentageOp ? "sm" : "md"}
                                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                            setFieldValue(
                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].operator`,
                                              e.target.value
                                            );
                                            // Limpa valor quando o operador muda
                                            setFieldValue(
                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`,
                                              ""
                                            );
                                          }}
                                        >
                                          {handleOperator(set, ruleIndex)}
                                        </Field>
                                        <FormErrorMessage>
                                          {getFormError(errors, [
                                            "criteriaSets",
                                            criteriaSetsIndex,
                                            "criteriaRules",
                                            ruleIndex,
                                            "operator",
                                          ])}
                                        </FormErrorMessage>
                                      </FormControl>

                                      {/* <FormControl
                                      isInvalid={!!getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'dataSource']) && touched.criteriaSets?.[criteriaSetsIndex]?.criteriaRules?.[ruleIndex]?.dataSource}
                                    >
                                      <FormLabel>Fonte de Dados</FormLabel>
                                      <Field
                                        as={Select}
                                        name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].dataSource`}
                                        placeholder="Selecione uma fonte de dados"
                                      >
                                        <option value="option1">Option 1</option>
                                        <option value="option2">Option 2</option>
                                      </Field>
                                        <FormErrorMessage>
                                        {getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'dataSource'])}
                                      </FormErrorMessage>
                                    </FormControl>

                                    <FormControl
                                      isInvalid={!!getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'field']) && touched.criteriaSets?.[criteriaSetsIndex]?.criteriaRules?.[ruleIndex]?.field}
                                    >
                                      <FormLabel>Campo</FormLabel>
                                      <Field
                                        as={Input}
                                        name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].field`}
                                        placeholder="Ex: idade"
                                      />
                                        <FormErrorMessage>
                                        {getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'field'])}
                                      </FormErrorMessage>
                                    </FormControl>

                                    <FormControl
                                      isInvalid={!!getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'operator']) && touched.criteriaSets?.[criteriaSetsIndex]?.criteriaRules?.[ruleIndex]?.operator}
                                    >
                                      <FormLabel>Operador</FormLabel>
                                      <Field
                                        as={Select}
                                        name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].operator`}
                                        placeholder="Selecione"
                                      >
                                        <option value="&gt;">maior que</option>
                                        <option value="&lt;">menor que</option>
                                        <option value="&gt;=">maior ou igual a</option>
                                        <option value="&lt;=">menor ou igual a</option>
                                        <option value="===">igual a</option>
                                      </Field>
                                      <FormErrorMessage>
                                        {getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'operator'])}
                                      </FormErrorMessage>
                                    </FormControl> */}

                                      <FormControl
                                        isInvalid={!!getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'value']) && touched.criteriaSets?.[criteriaSetsIndex]?.criteriaRules?.[ruleIndex]?.value}
                                      >
                                        {(() => {
                                          const currentField = set.criteriaRules[ruleIndex]?.field;
                                          const currentDataSource = set.criteriaRules[ruleIndex]?.dataSource;
                                          const currentOperator = set.criteriaRules[ruleIndex]?.operator;
                                          const currentValue = set.criteriaRules[ruleIndex]?.value || "";
                                          const field = currentField && currentDataSource
                                            ? getFieldByValue(currentDataSource, currentField)
                                            : null;

                                          const isPercentageOperator = [
                                            'PERCENTAGE_GREATER_EQUAL',
                                            'PERCENTAGE_LESS_EQUAL',
                                            'PERCENTAGE_GREATER',
                                            'PERCENTAGE_LESS',
                                            'PERCENTAGE_OF'
                                          ].includes(currentOperator);

                                          if (isPercentageOperator) {
                                            const valueParts = currentValue.split(',').map(v => v.trim());
                                            const referenceValue = valueParts[0] || "";
                                            const referenceType = valueParts[1] || "";
                                            const percentage = valueParts[2] || "";

                                            const isFixedValue = referenceType === "fixed_value";
                                            const referenceDataSource = !isFixedValue ? referenceType : "";

                                            const availableFields = referenceDataSource
                                              ? getFieldsForSelect(referenceDataSource)
                                              : [];

                                            return (
                                              <>
                                                <Flex gap={2} width="100%">
                                                  <Box width="80px">
                                                    <FormLabel fontSize="sm">%</FormLabel>
                                                    <Input
                                                      type="number"
                                                      min="0"
                                                      max="100"
                                                      step="0.01"
                                                      value={percentage || ""}
                                                      placeholder="0-100"
                                                      size="sm"
                                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        const newPercentage = e.target.value;
                                                        const newValue = `${referenceValue},${referenceType},${newPercentage}`;
                                                        setFieldValue(
                                                          `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`,
                                                          newValue
                                                        );
                                                      }}
                                                    />
                                                  </Box>
                                                  <Box minWidth="120px" width="120px">
                                                    <FormLabel fontSize="sm">Tipo</FormLabel>
                                                    <Select
                                                      value={isFixedValue ? "fixed_value" : "field"}
                                                      size="sm"
                                                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                        const newType = e.target.value;
                                                        let newValue;
                                                        if (newType === "fixed_value") {
                                                          newValue = `${referenceValue},fixed_value,${percentage}`;
                                                        } else {
                                                          newValue = `,,${percentage}`;
                                                        }
                                                        setFieldValue(
                                                          `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`,
                                                          newValue
                                                        );
                                                      }}
                                                    >
                                                      <option value="field">Campo</option>
                                                      <option value="fixed_value">Valor Fixo</option>
                                                    </Select>
                                                  </Box>

                                                  {isFixedValue ? (
                                                    <Box minWidth="140px" flex={1}>
                                                      <FormLabel fontSize="sm">Valor Base</FormLabel>
                                                      <Input
                                                        type="number"
                                                        value={referenceValue || ""}
                                                        placeholder="Ex: 10000"
                                                        size="sm"
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                          const newFixedValue = e.target.value;
                                                          const newValue = `${newFixedValue},fixed_value,${percentage}`;
                                                          setFieldValue(
                                                            `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`,
                                                            newValue
                                                          );
                                                        }}
                                                      />
                                                    </Box>
                                                  ) : (
                                                    <>
                                                      <Box minWidth="140px" flex={1}>
                                                        <FormLabel fontSize="sm">Fonte Base</FormLabel>
                                                        <Select
                                                          value={referenceDataSource || ""}
                                                          placeholder="Fonte"
                                                          size="sm"
                                                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            const newReferenceDataSource = e.target.value;
                                                            const newValue = `,${newReferenceDataSource},${percentage}`;
                                                            setFieldValue(
                                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`,
                                                              newValue
                                                            );
                                                          }}
                                                        >
                                                          {dataSources?.map((ds) => (
                                                            <option key={ds.value} value={ds.value}>
                                                              {ds.label}
                                                            </option>
                                                          ))}
                                                        </Select>
                                                      </Box>
                                                      <Box minWidth="140px" flex={1}>
                                                        <FormLabel fontSize="sm">Campo Base</FormLabel>
                                                        <Select
                                                          value={referenceValue || ""}
                                                          placeholder="Campo"
                                                          size="sm"
                                                          disabled={!referenceDataSource}
                                                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            const newReferenceField = e.target.value;
                                                            const newValue = `${newReferenceField},${referenceDataSource},${percentage}`;
                                                            setFieldValue(
                                                              `criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`,
                                                              newValue
                                                            );
                                                          }}
                                                        >
                                                          {availableFields.map((field) => (
                                                            <option key={field.value} value={field.value}>
                                                              {field.label}
                                                            </option>
                                                          ))}
                                                        </Select>
                                                      </Box>
                                                    </>
                                                  )}
                                                </Flex>
                                              </>
                                            );
                                          }

                                          return (
                                            <>
                                              <FormLabel>Valor</FormLabel>
                                              {(() => {
                                                // Se o campo for select (enum ou boolean), renderiza Select
                                                if (field && (field.tipo_input === "select" || field.tipo_input === "boolean") && field.opcoes) {
                                                  return (
                                                    <Field
                                                      as={Select}
                                                      name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`}
                                                      placeholder="Selecione um valor"
                                                    >
                                                      {field.opcoes.map((opcao) => (
                                                        <option key={opcao.valor} value={opcao.valor}>
                                                          {opcao.label}
                                                        </option>
                                                      ))}
                                                    </Field>
                                                  );
                                                }

                                                if (field?.tipo_input === "date") {
                                                  return (
                                                    <Field
                                                      as={Input}
                                                      type="date"
                                                      name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`}
                                                      placeholder={field.placeholder || "DD/MM/AAAA"}
                                                    />
                                                  );
                                                }

                                                if (field?.tipo_input === "number") {
                                                  return (
                                                    <Field
                                                      as={Input}
                                                      type="number"
                                                      name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`}
                                                      placeholder={field.placeholder || "Ex: 20"}
                                                    />
                                                  );
                                                }

                                                return (
                                                  <Field
                                                    as={Input}
                                                    type={field?.tipo_input === "email" ? "email" : field?.tipo_input === "tel" ? "tel" : "text"}
                                                    name={`criteriaSets[${criteriaSetsIndex}].criteriaRules[${ruleIndex}].value`}
                                                    placeholder={field?.placeholder || "Ex: valor"}
                                                  />
                                                );
                                              })()}
                                            </>
                                          );
                                        })()}
                                        <FormErrorMessage>
                                          {getFormError(errors, ['criteriaSets', criteriaSetsIndex, 'criteriaRules', ruleIndex, 'value'])}
                                        </FormErrorMessage>
                                      </FormControl>

                                      <Flex flexDirection="column" gap={1} alignItems='center'>
                                        <Flex alignItems="center" gap={1}>
                                          <FormLabel mb={0}>Ordem:</FormLabel>
                                          <Text fontWeight="bold" fontSize="lg">
                                            {ruleIndex + 1}
                                          </Text>
                                        </Flex>
                                        <Flex gap={1}>
                                          <Button
                                            size="md"
                                            variant="ghost"
                                            p={3}
                                            isDisabled={ruleIndex === set.criteriaRules.length - 1}
                                            onClick={() => handleOrderCriteriaRuleDown(set, ruleIndex, criteriaSetsIndex, setFieldValue)}
                                          >
                                            <ArrowDownOutlined />
                                          </Button>
                                          <Button
                                            size="md"
                                            variant="ghost"
                                            p={3}
                                            isDisabled={ruleIndex === 0}
                                            onClick={() => handleOrderCriteriaRuleUp(set, ruleIndex, criteriaSetsIndex, setFieldValue)}
                                          >
                                            <ArrowUpOutlined />
                                          </Button>
                                          <CloseButton
                                            size="md"
                                            p={5}
                                            onClick={() => handleDeleteCriteriaRule.mutate({
                                              id: set.criteriaRules[ruleIndex].id!,
                                              criteriaSetIndex: criteriaSetsIndex
                                            })}
                                          />
                                        </Flex>
                                      </Flex>
                                    </Flex>
                                  );
                                })}
                              </Box>
                            )}
                          </FieldArray>
                        </Box>
                      ))}
                    </VStack>
                  </>
                )}
              </FieldArray>
              <Flex justifyContent='end' mt={4}>
                <Button type="submit" colorScheme="primary" gap={2}>
                  {handleCreateCriteriaEngine.isPending || handleUpdateCriteriaEngine.isPending ?
                    <>
                      <Spinner size='sm' /> {isCreate ? "Salvar" : "Atualizar"}
                    </>
                    :
                    isCreate ? "Salvar" : "Atualizar"
                  }

                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
}
